import type { FC } from 'react';
import React from 'react';
import {
  CircularProgress,
  Stack,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { Label } from '@pflegenavi/web-components';

import Logo from '../../assets/pn-logo-with-name-colored.svg';
import type {
  DirectDebitStatus,
  PaymentInitiatedBy,
  ResidentState,
} from '@pflegenavi/shared/api';
import {
  DashboardCardType,
  getDashboardCardType,
} from '@pflegenavi/shared/frontend';

import { useTranslation } from 'react-i18next';

import AllSetImage from '../../assets/undraw-setup-up-direct-debit.svg';
import SettlementImage from '../../assets/undraw_settlement.svg';
import SettlementDirectDebitImage from '../../assets/undraw_settlement_direct_debit.svg';
import { InactiveDirectDebitInfo } from './infoBox/InactiveDirectDebitInfo';
import { SettlementInfo } from './infoBox/SettlementInfo';
import { DirectDebitInactiveInfoBox } from './infoBox/DirectDebitInactiveInfoBox';
import { LowBalanceManualPaymentInfoBox } from './infoBox/LowBalanceManualPaymentInfoBox';
import { DisabledPaymentInfoBox } from './infoBox/DisabledPaymentInfoBox';
import { DisabledPaymentForNursingHomeInfoBox } from './infoBox/DisabledPaymentForNursingHomeInfoBox';

export interface InfoBoxProps {
  resident:
    | {
        id: string;
        state: ResidentState;
        nursingHome: {
          phone: string;
        };
        activeBalance?: {
          amount: number;
          thresholdDueDate: Date | null;
        };
      }
    | undefined;
  isResidentLoading: boolean;
  handleManualPayment: () => void;
  directDebitStatus: DirectDebitStatus;
  initiatedBy: PaymentInitiatedBy | undefined;
  hasPendingCharge: boolean;
  paymentsDisabledForNursingHomeUntil: Date | undefined;
}

export const InfoBox: FC<InfoBoxProps> = (props) => {
  return <InfoBoxView {...props} />;
};

export type InfoBoxViewProps = InfoBoxProps;

export const InfoBoxView: FC<InfoBoxViewProps> = ({
  resident,
  isResidentLoading,
  handleManualPayment,
  directDebitStatus,
  initiatedBy,
  hasPendingCharge,
  paymentsDisabledForNursingHomeUntil,
}) => {
  if (isResidentLoading) {
    return <LoadingInfoBox />;
  }

  const cardType = getDashboardCardType({
    residentState: resident?.state,
    paymentInitiatedBy: initiatedBy,
    directDebitStatus,
    hasPendingCharges: hasPendingCharge,
    hasThresholdDueDate: Boolean(resident?.activeBalance?.thresholdDueDate),
    isDisabledPaymentForNursingHome:
      paymentsDisabledForNursingHomeUntil != null,
  });

  switch (cardType) {
    case DashboardCardType.DisabledPayment:
      return <DisabledPaymentInfoBox />;
    case DashboardCardType.Settlement:
      return <SettlementInfoBox />;
    case DashboardCardType.Archived:
      return <InactiveInfoBox />;
    case DashboardCardType.SetUpDirectDebit:
      return <DirectDebitInactiveInfoBox />;
    case DashboardCardType.DisabledPaymentNursingHome:
      return (
        <DisabledPaymentForNursingHomeInfoBox
          until={paymentsDisabledForNursingHomeUntil}
        />
      );
    case DashboardCardType.ManualPaymentLowBalance:
      return (
        <LowBalanceManualPaymentInfoBox
          handleClickButton={handleManualPayment}
        />
      );
    case DashboardCardType.None:
    default:
      return <AllSetInfoBox />;
  }
};

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface LoadingInfoBoxProps {}

export const LoadingInfoBox: FC<LoadingInfoBoxProps> = () => {
  return (
    <InfoBoxContainer
      showWelcomeMessage
      image={
        <img src={AllSetImage} alt="" width={210} style={{ marginTop: -30 }} />
      }
      content={
        <Stack
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <CircularProgress color="primary" sx={{ mt: 3 }} />
        </Stack>
      }
    />
  );
};

const WelcomeMessage: FC = () => {
  const { t } = useTranslation();
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        mb: 1.5,
      }}
    >
      <Typography variant="h4" sx={{ mr: 1 }}>
        {t('welcome-to')}
      </Typography>
      <img src={Logo} alt="" height={31} />
    </Stack>
  );
};

const AllSetContent: FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <Label
        sx={{
          mb: 2,
          p: 1.8,
        }}
        variant="filled"
        color="secondary"
      >
        Infoboard
      </Label>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {t('mobile.family-member.all-set')}
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        {t('dashboard.resident-active')}
      </Typography>
    </>
  );
};

export const SettlementInfoBox: FC = () => {
  return (
    <InfoBoxContainer
      image={
        <img
          src={SettlementImage}
          alt=""
          width={150}
          height={150}
          style={{ marginTop: 30 }}
        />
      }
      content={<SettlementInfo />}
    />
  );
};

export const InactiveInfoBox: FC = () => {
  return (
    <InfoBoxContainer
      image={
        <img
          src={SettlementDirectDebitImage}
          alt=""
          width={210}
          style={{ marginTop: 30 }}
        />
      }
      content={<InactiveDirectDebitInfo />}
    />
  );
};

export const AllSetInfoBox: FC = () => {
  return (
    <InfoBoxContainer
      showWelcomeMessage
      image={
        <img src={AllSetImage} alt="" width={210} style={{ marginTop: -30 }} />
      }
      content={<AllSetContent />}
    />
  );
};

export interface InfoBoxContainerProps {
  image: React.ReactNode;
  content: React.ReactNode;
  showWelcomeMessage?: boolean;
}

export const InfoBoxContainer: FC<InfoBoxContainerProps> = ({
  image,
  content,
  showWelcomeMessage,
}) => {
  const largeScreens = useMediaQuery('(min-width:750px)');
  return (
    <BoxContainer direction="column" justifyContent="space-between" gap={2}>
      {showWelcomeMessage && <WelcomeMessage />}
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Stack
          direction="column"
          sx={{
            alignItems: 'flex-start',
            flexGrow: 1,
          }}
        >
          {content}
        </Stack>
        {largeScreens && image}
      </Stack>
    </BoxContainer>
  );
};

const BoxContainer = styled(Stack)(({ theme }) =>
  theme.unstable_sx({
    bgcolor: 'primary.lighter',
    minHeight: 255,
    height: '100%',
    borderRadius: 4,
    paddingX: 3,
    paddingY: 3,
    boxShadow: '-2px 5px 20px -5px rgba(150,170,180,0.5)',
  })
);
